import styled from "@emotion/styled";
import { DSSelect } from "@hundred5/design-system";
import { Trans } from "@lingui/react/macro";
import React, { useEffect, useRef, useState } from "react";
import { useLatest } from "react-use";
import { getLanguages } from "./languages";

interface Props {
  skill?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onTimeout?: () => void;
}

const EDITOR_TIMEOUT = 10000;

export function CodeEditor({
  skill,
  onTimeout,
  value,
  onBlur,
  onChange,
}: Props) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onTimeoutRef = useLatest(onTimeout);
  const languages = getLanguages();
  const preSelectedLanguage =
    languages.find((item) => item.id === skill?.toLowerCase())?.id ??
    "plaintext";
  const [language, setLanguage] = useState(preSelectedLanguage);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      onTimeoutRef.current?.();
    }, EDITOR_TIMEOUT);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [onTimeoutRef]);

  return (
    <Container>
      <Language>
        <LanguageLabel htmlFor="code-editor-language">
          <Trans>Syntax highlighting:</Trans>
        </LanguageLabel>

        <LanguageSelectWrapper>
          <DSSelect
            id="code-editor-language"
            selectedItemId={language}
            onChange={(event) => {
              if (event) setLanguage(event.id);
            }}
            items={languages}
          />
        </LanguageSelectWrapper>
      </Language>

      <Editor>
        <React.Suspense
          fallback={
            <LoadingFallback>
              <Trans>Loading code editor...</Trans>
            </LoadingFallback>
          }
        >
          <LazyMonacoEditor
            language={language}
            value={value}
            options={editorOptions}
            onMount={(editor) => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              if (onBlur) {
                editor.onDidBlurEditorWidget(onBlur);
              }
            }}
            onChange={(value) => {
              if (value) {
                onChange(value);
              }
            }}
          />
        </React.Suspense>
      </Editor>
    </Container>
  );
}

//region Utils
const editorOptions = {
  minimap: { enabled: false },
};
//endregion

//region Styles
const Container = styled.div``;

const Language = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 12px;
  padding-top: 16px;
  margin-bottom: 20px;
`;

const LanguageLabel = styled.label`
  margin-right: 10px;
`;

const LanguageSelectWrapper = styled.div`
  width: 160px;

  @media (min-width: 768px) {
    width: 292px;
  }
`;

const Editor = styled.div`
  margin-bottom: 20px;
  height: 300px;
`;

const LoadingFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0.5;
  font-style: italic;
`;
//endregion
const LazyMonacoEditor = React.lazy(
  () => import(/* webpackChunkName: "monaco" */ "@monaco-editor/react"),
);

// const LazyMonacoEditor = dynamic(import("react-monaco-editor"), {
//   ssr: false,
// });
